// src/pages/Home.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  Container,
  Button,
  Spinner,
  Alert,
  Row,
  Col,
  Form,
  InputGroup,
  Badge,
} from "react-bootstrap";
import {
  collection,
  getDocs,
  doc,
  writeBatch,
  serverTimestamp,
  increment,
} from "firebase/firestore";
import { db, storage } from "../firebase";
import { ref as storageRef, getDownloadURL } from "firebase/storage";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { sendRequestCreatedEmails } from "../services/emailNotifications";

const Home = () => {
  // ===== Medicamentos =====
  const [medications, setMedications] = useState([]);
  const [filteredMedications, setFilteredMedications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // ===== Noticias =====
  const [news, setNews] = useState([]);
  const [newsLoading, setNewsLoading] = useState(true);
  const [newsError, setNewsError] = useState("");

  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState("");
  const [filterOption, setFilterOption] = useState("all");
  const [reservingId, setReservingId] = useState(null);

  const categoryMeta = {
    analgesic: { label: "Analgésico", icon: "bi-capsule-pill", color: "primary" },
    antibiotic: { label: "Antibiótico", icon: "bi-shield-check", color: "success" },
    antihistamine: { label: "Antihistamínico", icon: "bi-lungs", color: "info" },
    "anti-inflammatory": { label: "Anti-inflamatorio", icon: "bi-activity", color: "warning" },
  };

  // ---------- Utilidades ----------
  const isExpiringSoon = (expirationDate) => {
    if (!expirationDate) return false;
    const expDate = new Date(expirationDate);
    const today = new Date();
    const diffTime = expDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 30;
  };

  const fmtDate = (val) => {
    try {
      const d = val?.toDate ? val.toDate() : new Date(val);
      if (Number.isNaN(d?.getTime?.())) return "Fecha no disponible";
      return d.toLocaleDateString("es-ES", {
        year: "numeric",
        month: "short",
        day: "2-digit",
      });
    } catch {
      return "Fecha no disponible";
    }
  };

  const pickTs = (n) => n?.publishedAt || n?.createdAt || null;

  const resolveNewsImage = async (item) => {
    if (item.imageUrl) return item.imageUrl;
    if (item.imagePath) {
      try {
        const url = await getDownloadURL(storageRef(storage, item.imagePath));
        return url;
      } catch {
        return null;
      }
    }
    return null;
  };

  const clip = (text, max = 160) => {
    if (!text) return "";
    const s = String(text).trim();
    if (s.length <= max) return s;
    return s.slice(0, max - 1) + "…";
  };

  const stats = useMemo(() => {
    const inStock = medications.filter((m) => m.quantity > 0).length;
    const outOfStock = medications.filter((m) => m.quantity <= 0).length;
    const expiring = medications.filter((m) => isExpiringSoon(m.expirationDate)).length;
    return {
      total: medications.length,
      inStock,
      outOfStock,
      expiring,
    };
  }, [medications]);

  // ---------- Cargar Medicamentos ----------
  useEffect(() => {
    const fetchMedications = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "medications"));
        const meds = [];
        querySnapshot.forEach((d) => {
          const data = d.data();
          meds.push({
            id: d.id,
            ...data,
            quantity: typeof data.quantity === "number" ? data.quantity : 0,
          });
        });
        setMedications(meds);
        setFilteredMedications(meds);
      } catch (err) {
        setError("Error al cargar los medicamentos.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchMedications();
  }, []);

  // ---------- Cargar Noticias (Últimas Noticias) ----------
  useEffect(() => {
    const fetchNews = async () => {
      setNewsLoading(true);
      setNewsError("");
      try {
        const snap = await getDocs(collection(db, "news"));
        let rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

        // Solo publicadas (si no existe el campo, se considera publicada)
        rows = rows.filter((n) => n.published !== false);

        // Ordenar: destacadas primero, luego por fecha desc
        rows.sort((a, b) => {
          const ap = a?.pinned ? 1 : 0;
          const bp = b?.pinned ? 1 : 0;
          if (ap !== bp) return bp - ap;
          const ta = pickTs(a);
          const tb = pickTs(b);
          const da = ta?.toDate ? ta.toDate().getTime() : new Date(ta || 0).getTime();
          const dbb = tb?.toDate ? tb.toDate().getTime() : new Date(tb || 0).getTime();
          return dbb - da;
        });

        // Limitar a 3 para el carrusel del home
        rows = rows.slice(0, 3);

        // Resolver imágenes
        const withImgs = await Promise.all(
          rows.map(async (n) => ({
            ...n,
            _image: await resolveNewsImage(n),
          }))
        );

        setNews(withImgs);
      } catch (e) {
        console.error("Error cargando noticias:", e);
        setNewsError("No se pudieron cargar las noticias.");
      } finally {
        setNewsLoading(false);
      }
    };
    fetchNews();
  }, []);

  // ---------- Buscar/Filtrar Medicamentos ----------
  useEffect(() => {
    let results = medications;

    if (filterOption !== "all") {
      results = results.filter((med) => {
        if (filterOption === "expiringSoon") return isExpiringSoon(med.expirationDate);
        if (filterOption === "outOfStock") return med.quantity <= 0;
        if (filterOption === "inStock") return med.quantity > 0;
        return med.category === filterOption;
      });
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      results = results.filter(
        (med) =>
          med.name?.toLowerCase().includes(term) ||
          (med.description && med.description.toLowerCase().includes(term)) ||
          (med.dosage && med.dosage.toLowerCase().includes(term))
      );
    }

    setFilteredMedications(results);
  }, [searchTerm, filterOption, medications]);

  // ---------- Reservar ----------
  const handleReserve = async (medication) => {
    if (!currentUser) {
      alert("Debes iniciar sesión para reservar un medicamento.");
      navigate("/login");
      return;
    }

    if (medication.quantity <= 0) {
      setError("Este medicamento no tiene stock disponible.");
      return;
    }

    setReservingId(medication.id);
    setLoading(true);
    setError("");

    try {
      const batch = writeBatch(db);

      // 1) Disminuir stock
      const medRef = doc(db, "medications", medication.id);
      batch.update(medRef, {
        quantity: increment(-1),
        updatedAt: serverTimestamp(),
      });

      // 2) Crear request
      const requestRef = doc(collection(db, "requests"));
      batch.set(requestRef, {
        medicationId: medication.id,
        medicationName: medication.name,
        donorId: medication.donorId,
        donorEmail: medication.donorEmail,
        userId: currentUser.uid,
        userEmail: currentUser.email,
        status: "pendiente",
        quantity: 1,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      // 3) Subcolección del usuario
      const userReservationRef = doc(
        db,
        `users/${currentUser.uid}/reservations`,
        requestRef.id
      );
      batch.set(userReservationRef, {
        requestId: requestRef.id,
        medicationId: medication.id,
        medicationName: medication.name,
        status: "pending",
        quantity: 1,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      await batch.commit();

      await sendRequestCreatedEmails({
        requestId: requestRef.id,
        medicationName: medication.name,
        requesterEmail: currentUser.email,
        requesterName: currentUser.displayName || currentUser.email?.split("@")?.[0],
        donorEmail: medication.donorEmail,
        donorName: medication.donorName,
      });

      // Refrescar stock local
      setMedications((prev) =>
        prev.map((m) => (m.id === medication.id ? { ...m, quantity: m.quantity - 1 } : m))
      );

      navigate(`/solicitudes/${requestRef.id}`);
    } catch (err) {
      console.error("Error al reservar:", err);
      setError(err.message);
      if (err.code === "cancelled" || err.message?.includes("channel")) {
        setTimeout(() => handleReserve(medication), 2000);
      }
    } finally {
      setLoading(false);
      setReservingId(null);
    }
  };

  // ---------- Render ----------
  return (
    <Container className="py-4">
      <section className="mb-4">
        <Card className="border-0 shadow-sm overflow-hidden">
          <Card.Body
            className="p-4"
            style={{
              background: "linear-gradient(135deg, #0d6efd 0%, #198754 100%)",
              color: "#fff",
            }}
          >
            <Row className="align-items-center g-3">
              <Col lg={8}>
                <h1 className="mb-2 fw-bold d-flex align-items-center gap-2">
                  <i className="bi bi-capsule-pill" />
                  Medicamentos Disponibles
                </h1>
                <p className="mb-0 opacity-75">
                  Encontrá medicamentos, filtrá por categoría y reservá en segundos.
                </p>
              </Col>
              <Col lg={4}>
                <Row className="g-2">
                  <Col xs={6}>
                    <Card className="border-0 h-100">
                      <Card.Body className="py-2">
                        <div className="small text-muted">Total</div>
                        <div className="fw-bold fs-5">{stats.total}</div>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col xs={6}>
                    <Card className="border-0 h-100">
                      <Card.Body className="py-2">
                        <div className="small text-muted">Con stock</div>
                        <div className="fw-bold fs-5 text-success">{stats.inStock}</div>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col xs={6}>
                    <Card className="border-0 h-100">
                      <Card.Body className="py-2">
                        <div className="small text-muted">Sin stock</div>
                        <div className="fw-bold fs-5 text-danger">{stats.outOfStock}</div>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col xs={6}>
                    <Card className="border-0 h-100">
                      <Card.Body className="py-2">
                        <div className="small text-muted">Próximos a expirar</div>
                        <div className="fw-bold fs-5 text-warning">{stats.expiring}</div>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </section>

      {/* Buscador y filtros */}
      <div className="mb-4">
        <Row>
          <Col md={8} className="mb-3 mb-md-0">
            <InputGroup>
              <InputGroup.Text>
                <i className="bi bi-search-heart"></i>
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Buscar medicamentos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>
          </Col>
          <Col md={4}>
            <Form.Select
              value={filterOption}
              onChange={(e) => setFilterOption(e.target.value)}
            >
              <option value="all">Todos los medicamentos</option>
              <option value="inStock">Con stock disponible</option>
              <option value="outOfStock">Sin stock</option>
              <option value="expiringSoon">Próximos a expirar</option>
              <option value="analgesic">Analgésicos</option>
              <option value="antibiotic">Antibióticos</option>
              <option value="antihistamine">Antihistamínicos</option>
              <option value="anti-inflammatory">Anti-inflamatorios</option>
            </Form.Select>
          </Col>
        </Row>
      </div>

      {error && (
        <Alert variant="danger" onClose={() => setError("")} dismissible>
          {error}
        </Alert>
      )}

      {loading && medications.length === 0 ? (
        <div className="text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Cargando...</span>
          </Spinner>
        </div>
      ) : filteredMedications.length === 0 ? (
        <p className="text-center">
          No se encontraron medicamentos con los criterios seleccionados.
        </p>
      ) : (
        <Row className="g-3 row-cols-1 row-cols-sm-2 row-cols-lg-3 row-cols-xl-4 row-cols-xxl-5">
          {filteredMedications.map((med) => (
            <Col key={med.id}>
              <Card className="h-100 border-0 shadow-sm">
                {med.imageUrl ? (
                  <Card.Img
                    variant="top"
                    src={med.imageUrl}
                    alt={med.name}
                    style={{ height: 140, objectFit: "cover" }}
                  />
                ) : (
                  <div
                    className="d-flex align-items-center justify-content-center"
                    style={{
                      height: 140,
                      background: "linear-gradient(135deg, #eef2ff 0%, #e6f7ff 100%)",
                    }}
                  >
                    <i className="bi bi-capsule-pill fs-1 text-primary" />
                  </div>
                )}
                <Card.Body className="d-flex flex-column p-3">
                  <div className="d-flex justify-content-between align-items-start">
                    <Card.Title className="fs-6 mb-1">{med.name}</Card.Title>
                    {med.quantity <= 0 ? (
                      <Badge bg="danger" className="ms-2">Sin stock</Badge>
                    ) : (
                      <Badge bg="success" className="ms-2">{med.quantity}</Badge>
                    )}
                  </div>
                  <Card.Subtitle className="mb-2 text-muted small">
                    {med.dosage}
                  </Card.Subtitle>
                  {!!med.category && categoryMeta[med.category] && (
                    <div className="mb-2">
                      <Badge bg={categoryMeta[med.category].color} className="px-2 py-1 small">
                        <i className={`bi ${categoryMeta[med.category].icon} me-1`} />
                        {categoryMeta[med.category].label}
                      </Badge>
                    </div>
                  )}
                  <Card.Text className="flex-grow-1 small mb-2">
                    {!categoryMeta[med.category] && (
                      <>
                        <strong>Categoría:</strong> {med.category || "No especificada"}
                        <br />
                      </>
                    )}
                    <strong>Descripción:</strong> {med.description || "No disponible"}
                    <br />
                    <strong>Disponible hasta:</strong>{" "}
                    {med.expirationDate || "No especificado"}
                    {med.expirationDate && isExpiringSoon(med.expirationDate) && (
                      <Badge bg="warning" text="dark" className="ms-2">
                        Próximo a expirar
                      </Badge>
                    )}
                  </Card.Text>
                  <Button
                    variant="primary"
                    onClick={() => handleReserve(med)}
                    disabled={(loading && reservingId === med.id) || med.quantity <= 0}
                    className="mt-auto btn-sm"
                  >
                    {loading && reservingId === med.id ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                        Reservando...
                      </>
                    ) : med.quantity <= 0 ? (
                      <>
                        <i className="bi bi-x-circle me-2" />
                        No disponible
                      </>
                    ) : (
                      <>
                        <i className="bi bi-bag-plus me-2" />
                        Reservar medicamento
                      </>
                    )}
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* ===== Últimas Noticias (Editorial) ===== */}
      <section className="mt-5">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h2 className="mb-0 d-flex align-items-center gap-2">
            <i className="bi bi-newspaper text-primary" />
            Últimas Noticias
          </h2>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => navigate("/noticias")}
          >
            Ver todas
          </Button>
        </div>

        {newsError && (
          <Alert
            variant="warning"
            onClose={() => setNewsError("")}
            dismissible
            className="mb-3"
          >
            {newsError}
          </Alert>
        )}

        {newsLoading ? (
          <div className="text-center py-3">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Cargando noticias...</span>
            </Spinner>
          </div>
        ) : news.length === 0 ? (
          <Card className="mb-4">
            <Card.Body className="text-center text-muted">
              Aún no hay noticias publicadas.
            </Card.Body>
          </Card>
        ) : (
          <Row className="g-3">
            {(() => {
              const featured = news[0];
              const secondary = news.slice(1);
              const featuredWhen = pickTs(featured);
              const featuredTitle = featured.title || "Sin título";
              const featuredSummary = featured.summary || featured.subtitle || featured.content || "";

              return (
                <>
                  <Col lg={secondary.length ? 8 : 12}>
                    <Card className="border-0 shadow-sm h-100 overflow-hidden">
                      {featured._image ? (
                        <Card.Img
                          src={featured._image}
                          alt={featuredTitle}
                          style={{ height: 320, objectFit: "cover" }}
                        />
                      ) : (
                        <div
                          className="d-flex align-items-center justify-content-center text-muted"
                          style={{ height: 320, background: "linear-gradient(135deg, #f8f9fa, #e9ecef)" }}
                        >
                          Sin imagen
                        </div>
                      )}
                      <Card.Body>
                        <div className="small text-muted mb-2">
                          {featuredWhen ? fmtDate(featuredWhen) : ""}
                        </div>
                        <Card.Title className="mb-2">{featuredTitle}</Card.Title>
                        <Card.Text className="text-muted">
                          {clip(featuredSummary, 200)}
                        </Card.Text>
                        <Button size="sm" variant="primary" onClick={() => navigate(`/noticias/${featured.id}`)}>
                          <i className="bi bi-arrow-right-circle me-2" />
                          Leer noticia
                        </Button>
                      </Card.Body>
                    </Card>
                  </Col>

                  {secondary.length > 0 && (
                    <Col lg={4}>
                      <div className="d-flex flex-column gap-3">
                        {secondary.map((n) => {
                          const when = pickTs(n);
                          const title = n.title || "Sin título";
                          const summary = n.summary || n.subtitle || n.content || "";
                          return (
                            <Card key={n.id} className="border-0 shadow-sm">
                              <Card.Body>
                                <div className="small text-muted mb-1">{when ? fmtDate(when) : ""}</div>
                                <Card.Title className="fs-6 mb-1">{title}</Card.Title>
                                <Card.Text className="small text-muted mb-2">{clip(summary, 90)}</Card.Text>
                                <Button
                                  size="sm"
                                  variant="outline-primary"
                                  onClick={() => navigate(`/noticias/${n.id}`)}
                                >
                                  Ver
                                </Button>
                              </Card.Body>
                            </Card>
                          );
                        })}
                      </div>
                    </Col>
                  )}
                </>
              );
            })()}
          </Row>
        )}
      </section>
      {/* ⬆️ Esta sección queda antes del Footer porque Home se renderiza dentro del layout que
          coloca el Footer fuera del <main>. */}
    </Container>
  );
};

export default Home;
